import Layout from "@/HOC";
import React, { useMemo } from "react";
import styles from "./faq.module.css";
import { Breadcrumb, Collapse, Empty, Row, Space } from "antd";
import type { CollapseProps } from "antd";
import { getSiteContent } from "@/utils/apis/site-settings/site-settings.api";
import { IFaq } from "@/interfaces/site-settings.interface";
import Link from "next/link";
import { useAppContext } from "@/context";
import { extractPlainTextFromRichText, isNotSSR } from "@/utils/common";
import { useRouter } from "next/router";
import Head from "next/head";
import { getFaqSeo } from "@/utils/apis/minerals/article-seo.api";
import { DefaultCanonical, PageKeywords, metaDescription, metaTitle } from "@/component/seo/DefaultSeo";

function FAQ({
  data,
  seoData,
}: {
  data: CollapseProps["items"];
  seoData: any;
}) {
  console.log("FAQ SEO Data:", seoData);
  const { user } = useAppContext();
  const router = useRouter();

  const currentPageName = router.pathname.replace(/^\//, "").replace(/-/g, " ");
  const formattedPageName = currentPageName.toUpperCase();

  const parsedItems = useMemo(() => {
    return data?.map((item) => ({
      ...item,
      label: extractPlainTextFromRichText(item.label as string),
      children: (
        <div
          dangerouslySetInnerHTML={{
            __html: item.children as string,
          }}
        />
      ),
    }));
  }, [data]);


  const originalTitle = seoData?.title || formattedPageName;
  const seoTitle = [
    originalTitle,
    seoData?.description || "",
    seoData?.keywords || "",
  ]
    .filter(Boolean)
    .join(" | ");

  return (
    <div>
      <DefaultCanonical url={seoData?.canonicalUrl} />
      <PageKeywords keywords={seoData?.keywords} title={originalTitle} description={seoData?.description || ""} />
      <Head>
        <title key="title">{metaTitle(originalTitle, seoData?.title)}</title>
        <meta
          key="description"
          name="description"
          content={metaDescription(seoData?.description || "", seoData?.description)}
        />
        <meta
          key="og:title"
          property="og:title"
          content={seoData?.title || formattedPageName}
        />
        <meta
          key="og:description"
          property="og:description"
          content={metaDescription(seoData?.description || "", seoData?.description)}
        />
        <meta key="og:type" property="og:type" content="website" />
        <meta key="twitter:card" name="twitter:card" content="summary_large_image" />
        <meta
          key="twitter:title"
          name="twitter:title"
          content={seoData?.title || formattedPageName}
        />
        <meta
          key="twitter:description"
          name="twitter:description"
          content={seoData?.description || ""}
        />
        <meta
          key="viewport"
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
        />
      </Head>
      <Layout>
        <div className="banner"></div>
        <div className="Breadcrumb">
          <div className="main">
            <Breadcrumb
              items={[
                {
                  title: (
                    <Link href={user ? "/dashboard" : "/home"}>
                      {user ? "Dashboard" : "Home"}
                    </Link>
                  ),
                },
                {
                  title: <a href="">FAQ</a>,
                },
              ]}
            />
          </div>
        </div>
        <div>
          <div className="main">
            <div style={{ display: "flex", alignItems: "center", marginBottom: "20px", marginTop: "20px" }}>
              <h1 style={{ margin: 0, fontSize: "28px", fontWeight: "bold" }}>FAQ</h1>
            </div>
            {isNotSSR && (
              <Row>
                <div className="MainFaq">
                  {!data?.length ? (
                    <Space
                      style={{
                        width: "100%",
                        height: "16vmax",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        color: "#000",
                      }}
                    >
                      <Empty />
                    </Space>
                  ) : (
                    <Collapse items={parsedItems} />
                  )}
                </div>
              </Row>
            )}
          </div>
        </div>
      </Layout>
    </div>
  );
}

export const getServerSideProps = async () => {
  try {
    const [faqContentRes, seoRes] = await Promise.all([
      getSiteContent("faq"),
      getFaqSeo("faq").catch((err) => {
        console.error("Error fetching FAQ SEO:", err);
        return { data: { success: false } };
      }),
    ]);

    let data: CollapseProps["items"] = [];
    if (faqContentRes.data.success) {
      const content = faqContentRes.data?.data || [];
      data = content.map((item: IFaq) => ({
        key: item._id,
        label: item.question,
        children: item.answer,
      }));
    }

    let seoData = null;
    if (seoRes.data.success) {
      const rawSeoData = seoRes.data.data;
      // Handle case where API might return an array
      seoData = Array.isArray(rawSeoData) ? rawSeoData[0] : rawSeoData;
      console.log("FAQ SEO Data (Processed):", seoData);
    }

    return {
      props: {
        data,
        seoData,
      },
    };
  } catch (error) {
    console.error("Error fetching data:", error);
    return {
      props: {
        data: [],
        seoData: null,
      },
    };
  }
};

export default FAQ;
